import React, { useState } from 'react';

import { useGetGroup, useGetPermissionTags } from '@vlabs/api/artemy/client';
import type {
  GroupId,
  GroupRecord,
  PermissionTag,
} from '@vlabs/api/artemy/model';
import { Control } from '@vlabs/uikit';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { ValueSelect } from '@vlabs/monokit/shared/ui/value-select';

import st from './DetachPermissionTagGroup.module.sass';
import { useDetachPermissionTagGroupTranslation } from './i18n';

const displayName = 'DetachPermissionTagGroup';
const testid = buildTestid(displayName);

interface DetachPermissionTagGroupProps {
  groupId: GroupId;
  isPending?: boolean;
  onCancel: VoidFunction;
  onSubmit: (selection: DetachPermissionTagGroupSelection) => void;
}

export interface DetachPermissionTagGroupSelection {
  group: GroupRecord;
  permissionTag: PermissionTag;
}

const PERMISSION_TAGS_PAGE_SIZE = 1000;

function DetachPermissionTagGroup({
  groupId,
  isPending = false,
  onCancel,
  onSubmit,
}: DetachPermissionTagGroupProps): React.JSX.Element {
  const { t } = useDetachPermissionTagGroupTranslation();
  const getGroup = useGetGroup(groupId);
  const getPermissionTags = useGetPermissionTags({ page_size: PERMISSION_TAGS_PAGE_SIZE });

  const [permissionTag, setPermissionTag] = useState<PermissionTag | undefined>();
  const groupName = getGroup.data?.group_name ?? groupId;
  const isLoading = getGroup.isLoading || getPermissionTags.isLoading;
  const hasLoadError = getGroup.isError || getPermissionTags.isError;

  const permissionTagOptions = (getPermissionTags.data?.permission_tags ?? [])
    .filter(({ permission_tag: optionPermissionTag }) => optionPermissionTag !== getGroup.data?.permission_tag)
    .map(({ permission_tag: optionPermissionTag }) => ({
      label: optionPermissionTag,
      value: optionPermissionTag,
    }));

  const isSubmitDisabled = !permissionTag
    || isLoading
    || hasLoadError
    || isPending
    || permissionTagOptions.length === 0;

  const noOptionsMessage = (): string => {
    if (hasLoadError) return t('load_error');

    return t('empty');
  };

  const handleSubmit = (): void => {
    if (!getGroup.data || !permissionTag) return;

    onSubmit({ group: getGroup.data, permissionTag });
  };

  return (
    <div
      className={st.Root}
      {...testid()}
    >
      <div className={st.Header}>
        <h5>{t('title')}</h5>
        <div className={st.Description}>{t('description', { groupName })}</div>
      </div>

      <div>
        <ValueSelect
          disabled={isLoading || hasLoadError || isPending}
          id="permissionTag"
          isLoading={isLoading}
          label={t('permission_tag')}
          name="permissionTag"
          noOptionsMessage={noOptionsMessage}
          onChange={(value) => setPermissionTag(value ? value as PermissionTag : undefined)}
          options={permissionTagOptions}
          placeholder={t('placeholder')}
          value={permissionTag as never}
          {...testid('permissionTag')}
        />
      </div>

      <div className={st.Actions}>
        <Control.Button
          className={st.Action}
          disabled={isSubmitDisabled}
          isLoading={isPending}
          onClick={handleSubmit}
          type="button"
          {...testid('submit')}
        >
          {t('submit')}
        </Control.Button>

        <Control.Button
          className={st.Action}
          disabled={isPending}
          kind="secondary"
          onClick={onCancel}
          type="button"
          variant="outlined"
          {...testid('cancel')}
        >
          {t('cancel')}
        </Control.Button>
      </div>
    </div>
  );
}

DetachPermissionTagGroup.displayName = displayName;

export { DetachPermissionTagGroup };
